import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Grid, Card, Box, IconButton, Paper, Snackbar, Alert, Backdrop, CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Stack, Typography } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// Icons
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';


function ClaimsAdd() {
  const navigate = useNavigate();
  const location = useLocation();
  const { claim_id, user_id } = useParams();
  const isEditMode = Boolean(claim_id && user_id);

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [viewPdfUrl, setViewPdfUrl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // State for uploads
  const [files, setFiles] = useState({
    attendanceSheet: [],
    stipendClaim: [],
    travelBills: [],
    reviewTaBills: []
  });

  // State for existing documents (from API in edit mode)
  const [existingDocs, setExistingDocs] = useState({
    attendanceSheet: [],
    stipendClaim: [],
    travelBills: [],
    reviewTaBills: []
  });

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Date limits
  const minDateLimit = dayjs().subtract(1, 'year').startOf('year');

  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  };

  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: me,
    retry: false,
  });

  // Fallback: Fetch complete user profile to extract reliable district data if cookie/token is incomplete
  const { data: fullUserData } = useQuery({
    queryKey: ["userDetails", meData?.user_id],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/${meData.user_id}`, {
        withCredentials: true,
      });
      return response.data.data;
    },
    enabled: !!meData?.user_id,
  });

  const userDistrict = meData?.district || fullUserData?.district || "";

  const handleFileChange = (e, category) => {
    const uploadedFiles = Array.from(e.target.files);

    // Check size limit: 25MB
    const MAX_SIZE = 25 * 1024 * 1024;
    for (const file of uploadedFiles) {
      if (file.size > MAX_SIZE) {
        setSnackbar({ open: true, message: `File too large: ${file.name}. Maximum size allowed is 25MB.`, severity: "error" });
        e.target.value = "";
        return;
      }
    }

    setFiles(prev => ({
      ...prev,
      [category]: [...prev[category], ...uploadedFiles]
    }));
  };

  const removeFile = (category, index) => {
    setFiles(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }));
  };

  const viewPdf = (file) => {
    const url = URL.createObjectURL(file);
    setViewPdfUrl(url);
  };

  const handlePreview = (url) => {
    if (!url) return;
    const cleanUrl = url.toLowerCase();
    const isWord = cleanUrl.endsWith(".doc") || cleanUrl.endsWith(".docx");

    if (isWord) {
      // Use Office Online Viewer with parameters to hide some UI elements if possible
      const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
        url
      )}&wdStartOnLook=1`;
      setViewPdfUrl(officeUrl);
    } else {
      setViewPdfUrl(url);
    }
  };

  const handleDownload = async (url, title) => {
    if (!url) return;
    try {
      const cleanUrl = url.split('?')[0].toLowerCase();
      const originalExt = cleanUrl.split('.').pop();
      // Only download extensions that are not pdf
      // If it is a word doc, download as is.
      // Actually we just want to download the file directly with its extension.

      let downloadExt = originalExt;
      if (originalExt.startsWith('doc')) {
        downloadExt = originalExt;
      }

      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${title.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD')}.${downloadExt}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({ open: true, message: "Download started!", severity: "success" });
    } catch (error) {
      console.error("Download failed", error);
      setSnackbar({ open: true, message: "Download failed.", severity: "error" });
    }
  };
  const removeExistingDoc = (category, index) => {
    setExistingDocs(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }));
  };

  const clearForm = () => {
    if (isEditMode) {
      navigate("/claims/list");
    } else {
      setSelectedDate(dayjs());
      setFiles({
        attendanceSheet: [],
        stipendClaim: [],
        travelBills: [],
        reviewTaBills: []
      });
    }
  };

  const handleSubmit = async () => {
    if (!meData?.user_id) {
      setSnackbar({ open: true, message: "User authentication failed.", severity: "error" });
      return;
    }

    // Validation for mandatory documents: At least ONE of the following must be present
    const hasAttendance = files.attendanceSheet.length > 0 || (isEditMode && existingDocs.attendanceSheet.length > 0);
    const hasStipend = files.stipendClaim.length > 0 || (isEditMode && existingDocs.stipendClaim.length > 0);
    const hasTravelBills = files.travelBills.length > 0 || (isEditMode && existingDocs.travelBills.length > 0);

    if (!hasAttendance && !hasStipend && !hasTravelBills) {
      setSnackbar({
        open: true,
        message: `Please upload at least one mandatory document (Attendance Sheet, Stipend Claim, or Travel Bills).`,
        severity: "warning"
      });
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();

      if (isEditMode) {
        // Edit mode - append basic fields
        formData.append("user_id", meData.user_id);
        formData.append("district_id", userDistrict);
        formData.append("month", selectedDate.format("YYYY-MM"));

        // Append new files if uploaded
        if (files.attendanceSheet.length > 0) {
          files.attendanceSheet.forEach(file => formData.append("attendance_sheet", file));
        }
        if (files.stipendClaim.length > 0) {
          files.stipendClaim.forEach(file => formData.append("stipend_sheet", file));
        }
        if (files.travelBills.length > 0) {
          files.travelBills.forEach(file => formData.append("travel_bills", file));
        }
        if (files.reviewTaBills.length > 0) {
          files.reviewTaBills.forEach(file => formData.append("review_ta_bills", file));
        }

        // Append existing documents as JSON (documents that weren't removed)
        formData.append("existing_attendance_sheet", JSON.stringify(existingDocs.attendanceSheet));
        formData.append("existing_stipend_claim", JSON.stringify(existingDocs.stipendClaim));
        formData.append("existing_travel_bills", JSON.stringify(existingDocs.travelBills));
        formData.append("existing_review_ta_bills", JSON.stringify(existingDocs.reviewTaBills));

        const response = await axios.put(
          `${process.env.REACT_APP_API_URL}/claims/edit/${claim_id}/${user_id}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true
          }
        );

        setSnackbar({ open: true, message: response.data.message || "Claim updated successfully!", severity: "success" });
      } else {
        // Add mode
        formData.append("user_id", meData.user_id);
        formData.append("district_id", userDistrict);
        formData.append("month", selectedDate.format("YYYY-MM"));

        files.attendanceSheet.forEach(file => formData.append("attendance_sheet", file));
        files.stipendClaim.forEach(file => formData.append("stipend_sheet", file));
        files.travelBills.forEach(file => formData.append("travel_bills", file));
        files.reviewTaBills.forEach(file => formData.append("review_ta_bills", file));

        const response = await axios.post(`${process.env.REACT_APP_API_URL}/claims/add`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true
        });

        setSnackbar({ open: true, message: response.data.message || "Claim submitted successfully!", severity: "success" });
      }

      setTimeout(() => navigate("/claims/list"), 2000);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'submit'} claim.`, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const FileList = ({ title, category }) => (
    files[category].length > 0 && (
      <MDBox mt={3} sx={{ maxWidth: '800px' }}>
        <MDTypography variant="caption" fontWeight="bold" color="success" display="block" mb={1} sx={{ textTransform: 'uppercase', borderBottom: '1px solid #eee', pb: 0.5 }}>
          {title} Uploaded Files:
        </MDTypography>
        {files[category].map((file, idx) => {
          const isPdf = file.name.toLowerCase().endsWith(".pdf");
          return (
            <Paper key={idx} sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#fff', border: '1px solid #eee' }} elevation={0}>
              <MDBox display="flex" alignItems="center" sx={{ maxWidth: '85%', overflow: 'hidden' }}>
                {isPdf ? (
                  <PictureAsPdfIcon color="error" sx={{ mr: 1.5, fontSize: 18 }} />
                ) : (
                  <DescriptionIcon color="info" sx={{ mr: 1.5, fontSize: 18 }} />
                )}
                <MDTypography variant="caption" noWrap fontWeight="medium" sx={{ fontSize: '0.8rem' }}>{file.name}</MDTypography>
              </MDBox>
              <MDBox display="flex" gap={1}>
                {isPdf && (
                  <Tooltip title="View PDF">
                    <IconButton size="small" onClick={() => viewPdf(file)} color="info">
                      <VisibilityIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => removeFile(category, idx)} color="error">
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </MDBox>
            </Paper>
          );
        })}
      </MDBox>
    )
  );

  FileList.propTypes = {
    title: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
  };

  // Component to display existing documents from API
  const ExistingDocsList = ({ title, category }) => {
    const getFileName = (url) => {
      if (!url) return "Document";
      const parts = url.split('/');
      const fileName = parts[parts.length - 1];
      // Extract original name before the hash
      const match = fileName.match(/^(.+?)-[a-zA-Z0-9]+\.(pdf|doc|docx|PDF|DOC|DOCX)$/);
      return match ? match[1] + '.' + match[2] : fileName;
    };

    return existingDocs[category].length > 0 && (
      <MDBox mt={2} sx={{ maxWidth: '800px' }}>
        <MDTypography variant="caption" fontWeight="bold" color="info" display="block" mb={1} sx={{ textTransform: 'uppercase', borderBottom: '1px solid #e3f2fd', pb: 0.5 }}>
          {title} Existing Files:
        </MDTypography>
        {existingDocs[category].map((url, idx) => {
          const fileName = getFileName(url);
          const isPdf = fileName.toLowerCase().endsWith(".pdf");
          return (
            <Paper key={idx} sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }} elevation={0}>
              <MDBox display="flex" alignItems="center" sx={{ maxWidth: '85%', overflow: 'hidden' }}>
                {isPdf ? (
                  <PictureAsPdfIcon color="error" sx={{ mr: 1.5, fontSize: 18 }} />
                ) : (
                  <DescriptionIcon color="info" sx={{ mr: 1.5, fontSize: 18 }} />
                )}
                <MDTypography variant="caption" noWrap fontWeight="medium" sx={{ fontSize: '0.8rem' }}>{fileName}</MDTypography>
              </MDBox>
              <MDBox display="flex" gap={1}>
                <Tooltip title="View Document">
                  <IconButton
                    size="small"
                    color="info"
                    onClick={() => handlePreview(url)}
                  >
                    <VisibilityIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => handleDownload(url, fileName)}
                  >
                    <DescriptionIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                {meData?.role_id === 1 && (
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => removeExistingDoc(category, idx)} color="error">
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </MDBox>
            </Paper>
          );
        })}
      </MDBox>
    );
  };

  ExistingDocsList.propTypes = {
    title: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
  };

  const UploadBox = ({ title, category, isRequired }) => (
    <Grid item xs={12} sm={6} md={3}>
      <Box
        sx={{
          border: '2px dashed #4caf50',
          borderRadius: 2,
          p: 2,
          textAlign: 'center',
          backgroundColor: '#f1f8e9',
          cursor: 'pointer',
          transition: '0.3s',
          height: '130px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': { backgroundColor: '#e8f5e9' }
        }}
        onClick={() => document.getElementById(`${category}-upload`).click()}
      >
        <DescriptionIcon sx={{ fontSize: 35, color: '#4caf50', mb: 0.5 }} />
        <MDTypography variant="button" display="block" fontWeight="bold" color="dark" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', lineHeight: 1.2 }}>
          {title} {isRequired && <span style={{ color: 'red' }}>*</span>}
        </MDTypography>
        <MDTypography variant="caption" color="text" sx={{ fontSize: '0.7rem' }}>(Max 25MB PDF/Word)</MDTypography>
        <input
          id={`${category}-upload`}
          type="file"
          accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFileChange(e, category)}
        />
      </Box>
    </Grid>
  );

  UploadBox.propTypes = {
    title: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    isRequired: PropTypes.bool,
  };

  // Fetch claim data in edit mode
  useEffect(() => {
    const fetchClaimData = async () => {
      if (isEditMode) {
        try {
          setLoading(true);
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/claims/get/${claim_id}/${user_id}`,
            { withCredentials: true }
          );

          if (response.data.status === 200) {
            const claimData = response.data.data;
            // Set the month from the fetched data
            setSelectedDate(dayjs(claimData.month, "YYYY-MM"));

            // Set existing documents
            setExistingDocs({
              attendanceSheet: Array.isArray(claimData.attendance_sheet) ? claimData.attendance_sheet : [],
              stipendClaim: Array.isArray(claimData.stipend_claim) ? claimData.stipend_claim : [],
              travelBills: Array.isArray(claimData.travel_bills) ? claimData.travel_bills : [],
              reviewTaBills: Array.isArray(claimData.review_ta_bills) ? claimData.review_ta_bills : []
            });
          }
        } catch (error) {
          setSnackbar({
            open: true,
            message: error.response?.data?.message || "Failed to fetch claim data.",
            severity: "error"
          });
        } finally {
          setLoading(false);
        }
      }
    };

    fetchClaimData();
  }, [isEditMode, claim_id, user_id]);

  // Handle auto-select month from notification
  useEffect(() => {
    if (location.state?.month) {
      const format = location.state.fromNotification ? "MMMM YYYY" : "YYYY-MM";
      const monthDate = dayjs(location.state.month, format);
      if (monthDate.isValid()) {
        setSelectedDate(monthDate);
      }
    }
  }, [location.state]);


  // If role is ADMIN (1), block access to attaching claims
  if (meData?.role_id === 1 || meData?.role_id === 2) {
    return (
      <DashboardLayout>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80vh"
          flexDirection="column"
        >
          <MDTypography variant="h4" color="error" fontWeight="bold">
            403 - Unauthorized
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Admins are not authorized to access this page.
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <MDBox mt={4} mb={3}>
        <MDBox mb={3} display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2}>
          <MDTypography variant="h4" fontWeight="bold">{isEditMode ? 'Edit Monthly Claims' : 'Attach Monthly Claims'}</MDTypography>
        </MDBox>

        <Card>
          <MDBox p={{ xs: 2, sm: 3, md: 4 }}>
            <Stack spacing={4}>
              {/* Region 1: Month Selection */}
              <MDBox borderBottom="1px solid #eee" pb={3}>
                <Grid container alignItems="flex-end" spacing={2}>
                  <Grid item xs={12} sm={8} md={4}>
                    <MDTypography variant="h6" fontWeight="medium" mb={1}>Select Claim Month</MDTypography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Month and Year"
                        views={["year", "month"]}
                        value={selectedDate}
                        onChange={(newValue) => setSelectedDate(newValue)}
                        minDate={minDateLimit}
                        maxDate={dayjs().add(1, 'year').endOf('year')}
                        shouldDisableYear={(year) => year.isAfter(dayjs(), 'year')}
                        shouldDisableMonth={(month) => month.isAfter(dayjs(), 'month')}
                        disabled={isEditMode}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            InputProps: {
                              readOnly: true,
                              sx: { fontSize: "14px", height: "45px" },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Region 2: Upload Box Row */}
              <MDBox>
                <MDTypography variant="h6" color="success" fontWeight="bold" mb={1}>Upload Section</MDTypography>
                <MDTypography variant="caption" color="text" display="block" mb={3}>
                  Please upload at least one mandatory document (Attendance, Stipend, or Travel).
                  Accepted formats: <b>PDF, DOC, DOCX</b> (Strictly). Max 25MB per file.
                </MDTypography>

                <Grid container spacing={3}>
                  <UploadBox title="Attendance Sheet" category="attendanceSheet" isRequired={false} />
                  <UploadBox title="Stipend Claim" category="stipendClaim" isRequired={false} />
                  <UploadBox title="Travel Bills" category="travelBills" isRequired={false} />
                  <UploadBox title="Review TA Bills" category="reviewTaBills" isRequired={false} />
                </Grid>
              </MDBox>

              {/* Region 3: Uploaded Files (Vertical Below Boxes) */}
              <MDBox>
                {/* Show existing documents in edit mode */}
                <ExistingDocsList title="Attendance Sheet" category="attendanceSheet" />
                <ExistingDocsList title="Stipend Claim" category="stipendClaim" />
                <ExistingDocsList title="Travel Bills" category="travelBills" />
                <ExistingDocsList title="Review TA Bills" category="reviewTaBills" />

                {/* Show newly uploaded files */}
                <FileList title="Attendance Sheet" category="attendanceSheet" />
                <FileList title="Stipend Claim" category="stipendClaim" />
                <FileList title="Travel Bills" category="travelBills" />
                <FileList title="Review TA Bills" category="reviewTaBills" />
              </MDBox>

              {/* Region 4: Buttons Right Aligned */}
              <Grid item xs={12}>
                <Divider sx={{ mt: 4, mb: 2 }} />
                <MDBox display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="flex-end" gap={2}>
                  <MDButton variant="outlined" color="error" onClick={clearForm} sx={{ minWidth: { xs: '100%', sm: 160 }, borderRadius: 2 }}>
                    Cancel / Clear
                  </MDButton>
                  <MDButton variant="gradient" color="info" onClick={handleSubmit} disabled={loading} sx={{ minWidth: { xs: '100%', sm: 160 }, borderRadius: 2 }}>
                    {loading ? (isEditMode ? "Updating..." : "Submitting...") : (isEditMode ? "Update Claim" : "Submit Claim")}
                  </MDButton>
                </MDBox>
              </Grid>
            </Stack>
          </MDBox>
        </Card>
      </MDBox>

      {/* PDF Viewer */}
      <Dialog open={Boolean(viewPdfUrl)} onClose={() => setViewPdfUrl(null)} maxWidth="lg" fullWidth>
        <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
          Document Viewer
          <IconButton onClick={() => setViewPdfUrl(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ minHeight: '60vh', p: 0 }}>
          {viewPdfUrl && <iframe src={viewPdfUrl} width="100%" height="600px" style={{ border: 'none' }} title="PDF Preview" />}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setViewPdfUrl(null)} color="dark">Close</MDButton>
        </DialogActions>
      </Dialog>


      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%', color: 'white !important' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default ClaimsAdd;
